import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Container,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation } from "react-router-dom";
import mainlogo from "../images/mainlogo1.png";

interface NavItem {
  name: string;
  path: string;
}

const navItems: NavItem[] = [
  { name: "Home", path: "/" },
  { name: "Service", path: "/service" },
  { name: "Contact", path: "/contact" },
  { name: "About", path: "/about" },
  { name: "Enquiry", path: "/enquiry" },
];

function Navbar() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer =
    (open: boolean) =>
      (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
          event.type === "keydown" &&
          ((event as React.KeyboardEvent).key === "Tab" ||
            (event as React.KeyboardEvent).key === "Shift")
        ) {
          return;
        }
        setDrawerOpen(open);
      };

  const drawerContent = (
    <Box
      sx={{
        width: { xs: "80vw", sm: "280px" },
        height: "100vh",
        bgcolor: "#fff",
        p: 3,
      }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ mb: 3, px: 1 }}>
        <Box
          component="img"
          src={mainlogo}
          alt="Sri Eswar Logistics Logo"
          sx={{ height: 60, width: "auto", objectFit: "contain" }}
        />
      </Box>
      <List>
        {navItems.map(({ name, path }) => (
          <ListItemButton
            component={Link}
            to={path}
            key={name}
            selected={location.pathname === path}
            sx={{
              borderRadius: "8px",
              mb: 0.5,
              minHeight: 48,
              "&.Mui-selected": {
                bgcolor: "#e8eff7",
              },
            }}
          >
            <ListItemText
              primary={
                <Typography
                  sx={{
                    fontWeight: location.pathname === path ? 700 : 500,
                    color: location.pathname === path ? "#002B6B" : "#444",
                    fontSize: "1rem",
                  }}
                >
                  {name}
                </Typography>
              }
            />
          </ListItemButton>
        ))}
        {/* Mobile Portal Login & Support Buttons */}
        <Box sx={{ mt: 3, px: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            variant="contained"
            component={Link}
            to="/login"
            fullWidth
            sx={{
              minHeight: 48,
              backgroundColor: "#002B6B",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              fontSize: "1rem"
            }}
          >
            Portal Login
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => (window.location.href = "tel:+6590828163")}
            sx={{
              minHeight: 48,
              backgroundColor: "#2ECC71",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              fontSize: "1rem"
            }}
          >
            Support Call
          </Button>
        </Box>
      </List>
    </Box>
  );

  return (
    <AppBar
      component="nav"
      position="fixed"
      sx={{
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Container maxWidth="xl" disableGutters>
        <Toolbar
          sx={{
            minHeight: { xs: 70, sm: 80, md: 85, lg: 95 },
            px: { xs: 2, sm: 3, md: 4 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={mainlogo}
              alt="Sri Eswar Logistics Logo"
              loading="lazy"
              sx={{
                height: { xs: 65, sm: 75, md: 80, lg: 90 },
                width: "auto",
                maxWidth: { xs: 160, sm: 200, md: 240, lg: 280 },
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Desktop Center Navigation */}
          {!isMobile && (
            <Box
              component="nav"
              aria-label="Main navigation"
              sx={{
                display: "flex",
                gap: { md: 2, lg: 4 },
                flexGrow: 1,
                justifyContent: "center",
                px: 2,
              }}
            >
              {navItems.map(({ name, path }) => (
                <Box
                  key={path}
                  component={Link}
                  to={path}
                  sx={{
                    textDecoration: "none",
                    color: location.pathname === path ? "#002B6B" : "#444",
                    fontWeight: 600,
                    fontSize: { md: "0.9rem", lg: "1.05rem" },
                    transition: "all 0.2s ease",
                    px: { md: 1, lg: 1.5 },
                    py: 1.5,
                    minHeight: 48,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "4px",
                    position: "relative",
                    "&:after": {
                      content: '""',
                      position: "absolute",
                      bottom: 8,
                      left: "15%",
                      width: location.pathname === path ? "70%" : "0%",
                      height: "2px",
                      backgroundColor: "#002B6B",
                      transition: "width 0.3s ease",
                    },
                    "&:hover": {
                      color: "#002B6B",
                      "&:after": { width: "70%" },
                    },
                  }}
                >
                  {name}
                </Box>
              ))}
            </Box>
          )}

          {/* Right Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 1.5, lg: 2 },
              flexShrink: 0,
            }}
          >
            {!isMobile ? (
              <>
                <Button
                  variant="contained"
                  component={Link}
                  to="/login"
                  sx={{
                    minHeight: 42,
                    backgroundColor: "#002B6B",
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "8px",
                    px: { md: 2, lg: 3 },
                    fontSize: { md: "0.85rem", lg: "0.95rem" },
                    "&:hover": { backgroundColor: "#001A40", transform: "translateY(-1px)" },
                    transition: "all 0.2s",
                  }}
                >
                  Portal Login
                </Button>
                <Button
                  variant="contained"
                  onClick={() => (window.location.href = "tel:+6590828163")}
                  sx={{
                    minHeight: 42,
                    backgroundColor: "#2ECC71",
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "8px",
                    px: { md: 2, lg: 3 },
                    fontSize: { md: "0.85rem", lg: "0.95rem" },
                    "&:hover": { backgroundColor: "#27AE60", transform: "translateY(-1px)" },
                    transition: "all 0.2s",
                  }}
                >
                  Support
                </Button>
              </>
            ) : (
              <IconButton
                onClick={toggleDrawer(true)}
                aria-label="Open navigation menu"
                sx={{
                  color: "#000",
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                <MenuIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{ keepMounted: true }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
}

export default Navbar;